import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllClients, createClient, updateClient } from '../../../api/administration';
import { toast } from 'react-toastify';
import AccountHeader from './components/AccountHeader';
import AccountTable from './components/AccountTable';
import AccountModal from './components/AccountModal';
import CompanyManagement from './CompanyManagement';

// Map UI account status to API format (uppercase)
const mapAccountStatusForApi = (uiStatus) => {
  if (!uiStatus) return null;
  const mapping = {
    'pending': 'PENDING',
    'active': 'ACTIVE',
    'closed': 'CLOSED',
  };
  return mapping[uiStatus.toLowerCase()] || uiStatus.toUpperCase();
};

// Map API account status to UI format (lowercase for radio value)
const mapApiAccountStatusToUI = (apiStatus) => {
  if (!apiStatus) return '';
  const statusLower = apiStatus.toLowerCase();
  const mapping = {
    'pending': 'pending',
    'active': 'active',
    'closed': 'closed',
  };
  return mapping[statusLower] || statusLower;
};

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // companiesClient lives in router location state so Sidebar can read it too
  const companiesClient = location.state?.companiesClient || null;

  const initialClient = {
    client_code: '',
    description: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    business_name: '',
    signup_date: '',
    cancel_date: '',
    account_status: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    activated_date: '',
    activated_by: '',
    total_licenses: '',
    active_licenses: '',
    total_users: '',
    active_users: '',
    inactive_users: '',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [currentClient, setCurrentClient] = useState(initialClient);
  const [clients, setClients] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);

  const openModalForNewClient = () => {
    setIsEditMode(false);
    setEditingClientId(null);
    setCurrentClient(initialClient);
    setIsModalOpen(true);
  };

  // Strip time from ISO datetime string for HTML date inputs (yyyy-MM-dd)
  const toDateInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const openModalForUpdate = (clientId) => {
    const clientData = clients.find(
      (c) => c.record_id === clientId || c.client_id === clientId || c.id === clientId || c.client_code === clientId
    );
    if (!clientData) {
      toast.error('Client not found');
      return;
    }

    console.log('Edit client - Full clientData:', clientData);

    setIsEditMode(true);
    setEditingClientId(clientData.record_id || clientData.client_id || clientData.id);
    setCurrentClient({
      client_code: clientData.client_code || '',
      description: clientData.description || '',
      first_name: clientData.first_name || '',
      middle_name: clientData.middle_name || '',
      last_name: clientData.last_name || '',
      email: clientData.email || '',
      phone: clientData.phone || '',
      mobile: clientData.mobile || '',
      business_name: clientData.business_name || '',
      signup_date: toDateInput(clientData.signup_date),
      cancel_date: toDateInput(clientData.cancel_date),
      account_status: mapApiAccountStatusToUI(clientData.account_status),
      address1: clientData.address1 || '',
      address2: clientData.address2 || '',
      city: clientData.city || '',
      state: clientData.state || '',
      zip: clientData.zip || '',
      activated_date: toDateInput(clientData.activated_date),
      activated_by: clientData.activated_by || '',
      total_licenses: clientData.total_licenses || '',
      active_licenses: clientData.active_licenses || '',
      total_users: clientData.total_users || '',
      active_users: clientData.active_users || '',
      inactive_users: clientData.inactive_users || '',
    });
    setIsModalOpen(true);
  };

  const handleDeleteClient = async (clientId) => {
    const clientData = clients.find(
      (c) => c.record_id === clientId || c.client_id === clientId || c.id === clientId
    );
    if (!clientData) {
      toast.error('Client not found');
      return;
    }
    try {
      await updateClient(clientId, {
        client_code: clientData.client_code,
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        account_status: 'DELETED',
      });
      toast.success('Account deleted successfully');
      getData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete account';
      toast.error(errorMessage);
    }
  };

  const handleManageCompanies = (clientId) => {
    const clientData = clients.find(
      (c) => c.record_id === clientId || c.client_id === clientId || c.id === clientId
    );
    if (!clientData) {
      toast.error('Client not found');
      return;
    }
    navigate('/admin/account', {
      replace: true,
      state: {
        companiesClient: {
          id: clientData.record_id || clientData.client_id || clientData.id,
          client_uid: clientData.unique_id || clientData.client_uid || '',
          business_name: clientData.business_name || '',
          full_name: [clientData.first_name, clientData.last_name].filter(Boolean).join(' '),
          client_code: clientData.client_code || '',
        },
      },
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingClientId(null);
    setCurrentClient(initialClient);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Log form data for debugging
      console.log('Form data received:', formData);

      // Helper to convert date to ISO 8601 format
      const toISODateTime = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toISOString();
      };

      // Helper to parse integer safely
      const toInteger = (value) => {
        if (!value && value !== 0) return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      // Build client data matching API schema (only include non-empty values)
      const clientData = {
        client_code: formData.client_code?.trim(),
        first_name: formData.first_name?.trim(),
        last_name: formData.last_name?.trim(),
      };

      // Add optional string fields only if they have values
      if (formData.description?.trim()) clientData.description = formData.description.trim();
      if (formData.middle_name?.trim()) clientData.middle_name = formData.middle_name.trim();
      if (formData.email?.trim()) clientData.email = formData.email.trim().toLowerCase();
      // Strip formatting from phone numbers (keep only digits)
      if (formData.phone?.trim()) {
        const phoneDigits = formData.phone.replace(/\D/g, '');
        if (phoneDigits) clientData.phone = phoneDigits;
      }
      if (formData.mobile?.trim()) {
        const mobileDigits = formData.mobile.replace(/\D/g, '');
        if (mobileDigits) clientData.mobile = mobileDigits;
      }
      if (formData.business_name?.trim()) clientData.business_name = formData.business_name.trim();
      if (formData.account_status?.trim()) clientData.account_status = mapAccountStatusForApi(formData.account_status.trim());
      if (formData.address1?.trim()) clientData.address1 = formData.address1.trim();
      if (formData.address2?.trim()) clientData.address2 = formData.address2.trim();
      if (formData.city?.trim()) clientData.city = formData.city.trim();
      if (formData.state?.trim()) clientData.state = formData.state.trim();
      if (formData.zip?.trim()) clientData.zip = formData.zip.trim();
      if (formData.activated_by?.trim()) clientData.activated_by = formData.activated_by.trim();

      // Add date fields in ISO 8601 format
      const signupDate = toISODateTime(formData.signup_date);
      if (signupDate) clientData.signup_date = signupDate;

      const cancelDate = toISODateTime(formData.cancel_date);
      if (cancelDate) clientData.cancel_date = cancelDate;

      const activatedDate = toISODateTime(formData.activated_date);
      if (activatedDate) clientData.activated_date = activatedDate;

      // Add integer fields only if valid
      const totalLicenses = toInteger(formData.total_licenses);
      if (totalLicenses !== null) clientData.total_licenses = totalLicenses;

      const activeLicenses = toInteger(formData.active_licenses);
      if (activeLicenses !== null) clientData.active_licenses = activeLicenses;

      const totalUsers = toInteger(formData.total_users);
      if (totalUsers !== null) clientData.total_users = totalUsers;

      const activeUsers = toInteger(formData.active_users);
      if (activeUsers !== null) clientData.active_users = activeUsers;

      const inactiveUsers = toInteger(formData.inactive_users);
      if (inactiveUsers !== null) clientData.inactive_users = inactiveUsers;

      // Log the data being sent to API
      console.log('Data being sent to API:', clientData);

      if (isEditMode) {
        await updateClient(editingClientId, clientData);
        toast.success('Client Updated Successfully');
      } else {
        await createClient(clientData);
        toast.success('Client Created Successfully');
      }

      closeModal();
      getData();
    } catch (error) {
      console.error('Error saving client:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to save client';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const response = await getAllClients(1, 100);
      console.log('Clients data:', response);

      if (response && response.data) {
        const data = response.data;
        // API returns: { message: "...", response: { data: [...], pageNum, pageSize } }
        const clientsList = data.response?.data || data.clients || data.data?.records || data.records || data.data || [];
        console.log('Clients list extracted:', clientsList);
        setClients(Array.isArray(clientsList) ? clientsList : []);
      } else if (Array.isArray(response)) {
        setClients(response);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Something went wrong while fetching clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter clients based on search term (client-side filtering)
  const filteredClients = clients.filter((client) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim().toLowerCase();
    return (
      fullName.includes(searchLower) ||
      client.business_name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.phone?.toLowerCase().includes(searchLower) ||
      client.client_code?.toLowerCase().includes(searchLower) ||
      client.city?.toLowerCase().includes(searchLower)
    );
  });

  // Show company management view when a client is selected
  if (companiesClient) {
    return (
      <CompanyManagement
        client={companiesClient}
        onBack={() => navigate('/admin/account', { replace: true })}
      />
    );
  }

  return (
    <div className="p-5">
      {/* Header with actions */}
      <div className="border border-gray-300 rounded-lg relative">
        <AccountHeader
          onReload={getData}
          onAddClient={openModalForNewClient}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <AccountTable
            data={filteredClients}
            onEditClient={openModalForUpdate}
            onDeleteClient={handleDeleteClient}
            onManageCompanies={handleManageCompanies}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Client Modal */}
      <AccountModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentClient}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Account;
